import { LegacyProductCode } from '@up2stay/up2promo-api-client';
import { cn } from '@up2stay/up2promo-ui';
import { Icon } from '@iconify/react';
import {
  Contact,
  ExternalLink,
  Link2,
  Music2,
  Star,
  UtensilsCrossed,
} from 'lucide-react';
import { createElement, type ComponentType } from 'react';

type IconComponent = ComponentType<{
  className?: string;
  'aria-hidden'?: boolean;
}>;

function iconify(iconId: string): IconComponent {
  const Comp = ({
    className,
    'aria-hidden': ariaHidden,
  }: {
    className?: string;
    'aria-hidden'?: boolean;
  }) => <Icon icon={iconId} className={className} aria-hidden={ariaHidden} />;
  Comp.displayName = `Iconify(${iconId})`;
  return Comp;
}

// Lucide v1 removed brand icons — use Simple Icons via Iconify for IG/FB.
const PRODUCT_TYPE_ICONS: Record<string, IconComponent> = {
  [LegacyProductCode.GO]: Star,
  [LegacyProductCode.IG]: iconify('simple-icons:instagram'),
  [LegacyProductCode.FB]: iconify('simple-icons:facebook'),
  [LegacyProductCode.TT]: Music2,
  [LegacyProductCode.ME]: UtensilsCrossed,
  [LegacyProductCode.CU]: Link2,
  [LegacyProductCode.BC]: Contact,
};

export function getProductTypeIcon(productType?: string): IconComponent {
  const key = productType?.trim() ?? '';
  return (key && PRODUCT_TYPE_ICONS[key]) || ExternalLink;
}

export type ProductTypeIconProps = {
  productType?: string;
  label: string;
  className?: string;
  /** Visual weight: list-leading tile vs inline badge glyph */
  size?: 'sm' | 'md';
};

/**
 * Quiet type mark — ink glyph on graphite tile. Color reserved for health/live.
 */
export function ProductTypeIcon({
  productType,
  label,
  className,
  size = 'md',
}: ProductTypeIconProps) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center border border-border bg-muted text-foreground',
        size === 'md' ? 'size-10 rounded-md' : 'size-5 rounded-sm',
        className,
      )}
      aria-hidden
      title={label}
    >
      {createElement(getProductTypeIcon(productType), {
        className: cn(size === 'md' ? 'size-5' : 'size-3'),
        'aria-hidden': true,
      })}
    </span>
  );
}
